import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Pencil, Trash2, CalendarDays } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { TableSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import { fr } from "../../locales/fr"
import { useAuth } from "../../context/AuthContext"
import { canManageTeamAndCourses } from "../../utils/roles"
import {
  getTrainers,
  createTrainer,
  updateTrainer,
  deleteTrainer,
} from "../../services/trainerService"

const emptyForm = {
  name: "",
  phone: "",
  specialization: "",
  salary: "",
  user_id: "",
}

export default function TeamCoaches() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEdit = canManageTeamAndCourses(user)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await getTrainers()
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setRows([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const startCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setShowForm(true)
  }

  const startEdit = (r) => {
    setEditingId(r.id)
    setForm({
      name: r.name || "",
      phone: r.phone || "",
      specialization: r.specialization || "",
      salary: r.salary != null ? String(r.salary) : "",
      user_id: r.userId != null ? String(r.userId) : "",
    })
    setShowForm(true)
  }

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      specialization: form.specialization.trim(),
      salary: parseFloat(form.salary) || 0,
    }
    const uid = form.user_id.trim()
    if (uid) payload.user_id = parseInt(uid, 10)
    else payload.user_id = null
    try {
      if (editingId) {
        await updateTrainer(editingId, payload)
      } else {
        await createTrainer(payload)
      }
      setShowForm(false)
      await load()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const remove = async (id) => {
    if (!window.confirm(fr.team.confirmDeleteCoach)) return
    try {
      await deleteTrainer(id)
      await load()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.team.coachesTitle}</h1>
          <p className="text-muted-foreground">{fr.team.coachesSubtitle}</p>
        </div>
        {canEdit && (
          <Button type="button" onClick={startCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {fr.team.add}
          </Button>
        )}
      </div>

      {canEdit && showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? fr.team.edit : fr.team.add}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.name}</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.phone}</label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.specialization}</label>
                <Input
                  value={form.specialization}
                  onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.salary}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.userId}</label>
                <Input
                  value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  placeholder="ex. 2"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" disabled={saving}>
                  {fr.common.save}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  {fr.common.cancel}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <TableSkeleton rows={5} />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6">
              <EmptyState title={fr.team.noCoaches} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fr.team.name}</TableHead>
                  <TableHead>{fr.team.phone}</TableHead>
                  <TableHead>{fr.team.specialization}</TableHead>
                  <TableHead>{fr.team.salary}</TableHead>
                  <TableHead>{fr.team.linkedUser}</TableHead>
                  <TableHead className="w-[140px]">{fr.team.coachCalendarCol}</TableHead>
                  {canEdit && <TableHead className="w-[120px]">{fr.common.actions}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.phone}</TableCell>
                    <TableCell>{r.specialization}</TableCell>
                    <TableCell>{r.salary}</TableCell>
                    <TableCell>{r.userId ?? fr.team.none}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/calendar/coach/${r.id}`)}
                      >
                        <CalendarDays className="mr-1 h-3.5 w-3.5" />
                        {fr.calendarPage.coachView}
                      </Button>
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(r)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => remove(r.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
